import { useState } from "react";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { toast } from "sonner";

const inputCls = "mt-1 bg-white/5 border-white/10 text-white/90 placeholder:text-white/20 focus:border-amber-400/50";

function PersonFields({ prefix, title, form, setForm }) {
  const key = (f) => (prefix ? `${prefix}_${f}` : f);
  const set = (f, v) => setForm((prev) => ({ ...prev, [key(f)]: v }));
  return (
    <div className="space-y-3 text-left">
      {title && <p className="text-white/60 text-xs tracking-[0.15em] uppercase">{title}</p>}
      <div>
        <Label className="text-white/40 text-xs">Name (optional)</Label>
        <Input value={form[key("name")] || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid={`${key("name")}-input`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-white/40 text-xs">Birth Date</Label>
          <Input type="date" value={form[key("birth_date")] || ""} onChange={(e) => set("birth_date", e.target.value)} className={inputCls} data-testid={`${key("birth_date")}-input`} />
        </div>
        <div>
          <Label className="text-white/40 text-xs">Birth Time</Label>
          <Input type="time" value={form[key("birth_time")] || ""} onChange={(e) => set("birth_time", e.target.value)} className={inputCls} data-testid={`${key("birth_time")}-input`} />
        </div>
      </div>
      <div>
        <Label className="text-white/40 text-xs">Birth City</Label>
        <CityAutocomplete placeholder="e.g., Mumbai, India" value={form[key("birth_city")] || ""} onChange={(v) => set("birth_city", v)} className={inputCls} testId={`${key("birth_city")}-input`} />
      </div>
    </div>
  );
}

export function ReportDetailsForm({ packageId, label, initial = {}, onSubmit, submitting, submitLabel, submittingLabel }) {
  const [form, setForm] = useState(initial);
  const isCompat = packageId === "compatibility" || packageId === "compatibility_report";
  const prefixes = isCompat ? ["person1", "person2"] : [""];

  const handleSubmit = () => {
    const missing = prefixes.some((p) =>
      ["birth_date", "birth_time", "birth_city"].some((f) => !form[p ? `${p}_${f}` : f]?.trim())
    );
    if (missing) {
      toast.error("Please enter birth date, time and city");
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="rounded-2xl p-5 mb-4 space-y-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="report-details-form">
      <p className="text-white/70 text-sm">Enter the birth details for your {label}</p>
      {isCompat ? (
        <>
          <PersonFields prefix="person1" title="Person 1" form={form} setForm={setForm} />
          <PersonFields prefix="person2" title="Person 2" form={form} setForm={setForm} />
        </>
      ) : (
        <PersonFields prefix="" form={form} setForm={setForm} />
      )}
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-white text-black hover:bg-white/90 rounded-full h-11 text-sm" data-testid="report-details-submit-btn">
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {submittingLabel || `Generating your ${label}…`}</>
        ) : (
          <>{!submitLabel && <Download className="w-4 h-4 mr-2" />} {submitLabel || `Generate ${label}`}</>
        )}
      </Button>
    </div>
  );
}
